/*
Simple 2D JavaScript Vector Class
Hacked from evanw's lightgl.js
https://github.com/evanw/lightgl.js/blob/master/src/vector.js
*/

class Vector {
  x: number;
  y: number;

  constructor(x: number, y: number) {
    this.x = x || 0;
    this.y = y || 0;
  }
  negative() {
    this.x = -this.x;
    this.y = -this.y;
    return this;
  }
  add(v: Vector | number) {
    if (v instanceof Vector) {
      this.x += v.x;
      this.y += v.y;
    } else {
      this.x += v;
      this.y += v;
    }
    return this;
  }
  subtract(v: Vector | number) {
    if (v instanceof Vector) {
      this.x -= v.x;
      this.y -= v.y;
    } else {
      this.x -= v;
      this.y -= v;
    }
    return this;
  }
  multiply(v: Vector | number) {
    if (v instanceof Vector) {
      this.x *= v.x;
      this.y *= v.y;
    } else {
      this.x *= v;
      this.y *= v;
    }
    return this;
  }
  divide(v: Vector | number) {
    if (v instanceof Vector) {
      if (v.x != 0) this.x /= v.x;
      if (v.y != 0) this.y /= v.y;
    } else {
      if (v != 0) {
        this.x /= v;
        this.y /= v;
      }
    }
    return this;
  }
  equals(v: Vector) {
    return this.x == v.x && this.y == v.y;
  }
  dot(v: Vector) {
    return this.x * v.x + this.y * v.y;
  }
  cross(v: Vector) {
    return this.x * v.y - this.y * v.x;
  }
  length() {
    return Math.sqrt(this.dot(this));
  }
  normalize() {
    return this.divide(this.length());
  }
  min() {
    return Math.min(this.x, this.y);
  }
  max() {
    return Math.max(this.x, this.y);
  }
  toAngles() {
    return -Math.atan2(-this.y, this.x);
  }
  angleTo(a: Vector) {
    return Math.acos(this.dot(a) / (this.length() * a.length()));
  }
  turn(radiant: number) {
    //let radiant = (degree * Math.PI) / 180;
    const newX = Math.cos(radiant) * this.x - Math.sin(radiant) * this.y;
    const newY = Math.sin(radiant) * this.x + Math.cos(radiant) * this.y;
    this.x = newX;
    this.y = newY;
    return this;
  }
  toArray(n: number) {
    return [this.x, this.y].slice(0, n || 2);
  }
  clone() {
    return new Vector(this.x, this.y);
  }
  set(x: number, y: number) {
    this.x = x;
    this.y = y;
    return this;
  }
  /* STATIC METHODS */
  static negative(v: Vector) {
    return new Vector(-v.x, -v.y);
  }
  static add(a: Vector, b: Vector | number) {
    if (b instanceof Vector) return new Vector(a.x + b.x, a.y + b.y);
    else return new Vector(a.x + b, a.y + b);
  }
  static subtract(a: Vector, b: Vector | number) {
    if (b instanceof Vector) return new Vector(a.x - b.x, a.y - b.y);
    else return new Vector(a.x - b, a.y - b);
  }
  static multiply(a: Vector, b: Vector | number) {
    if (b instanceof Vector) return new Vector(a.x * b.x, a.y * b.y);
    else return new Vector(a.x * b, a.y * b);
  }
  static divide(a: Vector, b: Vector | number) {
    if (b instanceof Vector) return new Vector(a.x / b.x, a.y / b.y);
    else return new Vector(a.x / b, a.y / b);
  }
  static equals(a: Vector, b: Vector) {
    return a.x == b.x && a.y == b.y;
  }
  static dot(a: Vector, b: Vector) {
    return a.x * b.x + a.y * b.y;
  }
  static cross(a: Vector, b: Vector) {
    return a.x * b.y - a.y * b.x;
  }
}

export default Vector;
